/**
 * Wire schemas for the kiosk/tablet check-in surface (`BACKLOG.md` C7, `emr-po` A16).
 *
 * Two audiences, two route families, two schema groups below:
 *
 *   - **Staff** (`POST /api/kiosk/tokens`, `POST /api/kiosk/tokens/:id/revoke`) — behind the ordinary
 *     staff session and `patient:read` (`server/src/kiosk/routes.ts`'s own header argues the
 *     capability choice). Creates/revokes the token; never touches patient data itself.
 *   - **Kiosk** (`GET`/`PATCH /api/kiosk/checkin`, `POST /api/kiosk/checkin/consents/accept`,
 *     `POST /api/kiosk/checkin/complete`) — unauthenticated in the ordinary sense (no staff session,
 *     no cookie), authenticated instead by the bearer token from the create response above, sent as
 *     `Authorization: Bearer <token>`. Every one of these takes no `patientId` anywhere in its request
 *     shape — path, query, or body — which is the structural property that makes "this token can only
 *     ever reach the one patient it was issued for" true by construction rather than by an
 *     equality check a future edit could drop. See `server/src/kiosk/middleware.ts`'s
 *     `requireKioskToken` for where the token resolves to a patient id, and every kiosk route handler
 *     for why none of them accept one from the caller.
 */

import { z } from 'zod';

import { kioskPatientResponseSchema, kioskUpdatePatientRequestSchema } from './clinicalRequests.ts';
import { legalDocumentStatusSchema, legalDocumentTypeSchema } from './legalDocuments.ts';

// --- Staff: issue / revoke -------------------------------------------------------------------------

/**
 * `documentTypes` is the manual-override escape hatch `emr-po` A16 decision 4 names — "a manual
 * override at token-creation time... not the primary mechanism." The primary mechanism (rules-based
 * auto-assignment by visit type/provider/location/patient attributes) is real follow-up work, not
 * built here (see `server/src/kiosk/service.ts`'s own header for why); omitting this field is what a
 * staff member does today, and the kiosk checkin route resolves it to "every patient-scoped legal
 * document type currently published" as the honest stand-in default.
 */
export const createKioskTokenRequestSchema = z.strictObject({
  patientId: z.uuid(),
  documentTypes: z.array(legalDocumentTypeSchema).min(1).optional(),
});
export type CreateKioskTokenRequest = z.infer<typeof createKioskTokenRequestSchema>;

export const kioskTokenResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  /** Present only on the create response — the one and only time the raw bearer value is ever sent.
   * Never present on a list/revoke response, matching `invites`' own `includeToken` convention. */
  token: z.string().optional(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type KioskTokenResponse = z.infer<typeof kioskTokenResponseSchema>;

export const createKioskTokenResponseSchema = z.strictObject({
  token: kioskTokenResponseSchema,
});
export type CreateKioskTokenResponse = z.infer<typeof createKioskTokenResponseSchema>;

export const KIOSK_TOKEN_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'forbidden',
  'audit_unavailable',
] as const;
export type KioskTokenErrorCode = (typeof KIOSK_TOKEN_ERROR_CODES)[number];

// --- Kiosk: check-in -------------------------------------------------------------------------------

export const kioskCheckinResponseSchema = z.strictObject({
  patient: kioskPatientResponseSchema,
  /** Every document this visit's token requires, each annotated with whether it is already accepted —
   * same shape `GET /api/legal-documents` already returns for a staff session, reused rather than
   * re-invented (`legalDocumentStatusSchema`), minus the `scope` field: every row here is `patient`
   * scope by construction (`server/src/legal/service.ts`'s `patientLegalDocumentStatuses` only ever
   * resolves patient-scoped types), so repeating a constant on every row would say nothing a reader
   * doesn't already know from being on this response at all. */
  consents: z.array(legalDocumentStatusSchema.omit({ scope: true })),
  /** Server clock, ISO instant — so the kiosk UI can render "you have about N minutes" without
   * trusting its own device clock, the same reasoning `sessions.expiresAt` being server-authoritative
   * already rests on. */
  expiresAt: z.string(),
});
export type KioskCheckinResponse = z.infer<typeof kioskCheckinResponseSchema>;

export const kioskUpdateCheckinRequestSchema = kioskUpdatePatientRequestSchema;
export type KioskUpdateCheckinRequest = z.infer<typeof kioskUpdateCheckinRequestSchema>;

export const kioskUpdateCheckinResponseSchema = z.strictObject({
  patient: kioskPatientResponseSchema,
});
export type KioskUpdateCheckinResponse = z.infer<typeof kioskUpdateCheckinResponseSchema>;

export const acceptKioskConsentRequestSchema = z.strictObject({
  documentType: legalDocumentTypeSchema,
});
export type AcceptKioskConsentRequest = z.infer<typeof acceptKioskConsentRequestSchema>;

export const acceptKioskConsentResponseSchema = z.strictObject({
  accepted: z.literal(true),
  documentType: legalDocumentTypeSchema,
  version: z.string(),
});
export type AcceptKioskConsentResponse = z.infer<typeof acceptKioskConsentResponseSchema>;

export const kioskCompleteResponseSchema = z.strictObject({
  completedAt: z.string(),
});
export type KioskCompleteResponse = z.infer<typeof kioskCompleteResponseSchema>;

/**
 * Every failure a kiosk (token-scoped) route can produce. `kiosk_token_invalid`/`kiosk_token_expired`
 * mirror `auth/middleware.ts`'s own `session_invalid`/`session_expired` split and the same reasoning:
 * a client that presented a real-but-dead token does not need to know *which* dead reason (revoked vs.
 * naturally expired vs. already completed) — `requireKioskToken`'s own comment covers what the audit
 * trail still records regardless. `consent_document_not_required` covers accepting a document type
 * this specific token's own consent list does not include — never a silent no-op, since that would
 * let a caller probe which document types exist for a patient it should not otherwise be able to
 * distinguish.
 */
export const KIOSK_CHECKIN_ERROR_CODES = [
  'invalid_request',
  'kiosk_token_invalid',
  'kiosk_token_expired',
  'mrn_taken',
  'not_found',
  'consent_document_not_required',
  'audit_unavailable',
] as const;
export type KioskCheckinErrorCode = (typeof KIOSK_CHECKIN_ERROR_CODES)[number];
